'use client';

import { useState } from 'react';
import Image from 'next/image';
import { SunIcon } from '@heroicons/react/16/solid';
import { buildContentstackImageUrl } from '@/lib/utils';
import { useModal } from '@/components/ModalContext';
import { useProduct } from '@/contexts/ProductContext';
import { usePricing } from '@/contexts/PricingContext';
import { formatPrice } from '@/utils/utilities.mjs';
import PriceDetails from '@/components/global/PriceDetails';
import SaveForLater from '@/components/product/SaveForLater';
import HsaMessage from '@/components/product/HsaMessage';
import AddToCartButton from '@/components/blocks/addToCartButton/AddToCartButton';
import ZipCodeChecker from '@/components/blocks/zipCodeChecker/ZipCodeChecker';
import { openZaiusCampaign } from '@/components/integrations/Zaius/Modal';
import V2ExtendedOptionButtonsModal from './V2ExtendedOptionButtonsModal';
import InfoIcon from '@/components/icons/InfoIcon';

const AFFIRM_MONTHS = 31;

// BL-652: matches ProductDetailsPrice.js's Affirm column (same logo, same
// monthly-estimate math, same modal-or-fallback-tab open behavior) — kept
// separate rather than imported since that component renders a two-column
// Pay-Now/Affirm layout the card's single-line reference doesn't use.
const AffirmLine = ({ price }) => {
  const monthlyPrice = price > 0 ? Math.ceil(price / AFFIRM_MONTHS) : 0;

  return (
    <p className="text-xs text-primary">
      As low as {formatPrice(monthlyPrice)}/MO Prequalify with{' '}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="https://cdn-assets.affirm.com/images/black_logo-transparent_bg.png"
        alt="Affirm"
        className="inline-block h-4.25 w-auto align-baseline"
      />
      .{' '}
      <button
        type="button"
        onClick={() => {
          if (typeof window.affirm?.ui?.openModal === 'function') {
            window.affirm.ui.openModal({ amount: Math.round(price * 100), pageType: 'product' });
          } else {
            window.open('https://www.affirm.com/apps/prequal', '_blank', 'noopener,noreferrer');
          }
        }}
        className="cursor-pointer text-sea underline"
      >
        Learn more
      </button>
    </p>
  );
};

const V2BuyBoxCard = ({ block, saveForLaterBlock, zipCodeCheckerBlock, atcButtonBlock, shippingMessage, productName }) => {
  const { openModal } = useModal();
  const { productData, selectedVariant, selectedVariantPrices } = useProduct();
  const pricing = usePricing();

  const {
    usps = [],
    show_finance_message,
    show_hsa_fsa_message,
    show_zip_code_checker,
    show_zip_code_checker_expanded,
    pitstop,
  } = block ?? {};

  // BL-652 review: title comes from the CMS `product_name`, which is not always
  // the Shopify title — the CMS drops the brand prefix on some products
  // ("Natural Mattress" against Shopify's "Birch Natural Mattress"). Shopify is
  // the fallback so a product with the CMS field empty still renders a title.
  const productTitle = productName || productData?.shopifyProduct?.title;
  const selectionSummary = (selectedVariant?.selectedOptions ?? [])
    .map((o) => o.value)
    .join(', ');

  const currencyCode = selectedVariant?.price?.currencyCode ?? 'USD';
  const fallbackPrice = parseFloat(selectedVariant?.price?.amount ?? 0);
  const price = selectedVariantPrices?.sale ?? fallbackPrice;
  const fullPrice = selectedVariantPrices?.full ?? fallbackPrice;
  const hasDiscount = selectedVariantPrices?.hasPriceDifference && fullPrice > price;
  const promotionalCopy = selectedVariant ? pricing.promotionalCopy(selectedVariant, 'compact') : null;

  // BL-652 review (2026-08-28): Richard added show_zip_code_checker /
  // show_zip_code_checker_expanded to the CMS — "IF TRUE both the zipcode
  // checker and pitstop will be visible on page." One toggle now drives both,
  // replacing the old show_in_home_setup_widget-based gating. Still guarded
  // by the actual block existing, so a true toggle with no data doesn't
  // render an empty checker.
  const showZipCodeChecker = !!show_zip_code_checker && !!zipCodeCheckerBlock;

  // BL-652 (2026-09-04): on Helix the pitstop folds away with the zip code
  // checker rather than sitting under a collapsed box. The disclosure state
  // lives in ZipCodeChecker, which reports every move through
  // onExpandedChange; this mirrors it so the sibling can follow. Seeded from
  // the same CMS field the checker starts from, so both agree on first paint.
  const [zipExpanded, setZipExpanded] = useState(!!show_zip_code_checker_expanded);
  const showPitstop = showZipCodeChecker && zipExpanded && pitstop?.heading_label;

  if (!selectedVariant) return null;

  return (
    // BL-784: comp specs square corners for the summary card, not rounded-2xl.
    // Padding is asymmetric (16px sides, 24px top/bottom), not p-6's uniform
    // 24px — confirmed via Figma MCP on the "Desktop" card frame (4300:31897).
    // Mobile-only drop-shadow per comp; desktop stays flat.
    // BL-801: no own mt/mb — this block only gets the 24px base gap from the
    // buy box column's gap-6, same as every other non-variant-selector block.
    <section className="flex flex-col gap-4 bg-white p-4 shadow-[0_4px_8px_rgba(0,0,0,0.16)] md:px-4 md:py-6 md:shadow-none font-worksans text-primary" data-block="buy_box_card">
      {/* BL-652 review (2026-08-28): Richard confirmed the repeat is intentional —
          matches the Helix reference, where the card carries its own title
          alongside the masthead above it. */}
      {/* BL-652 review: the title and its selection summary read as one unit on
          Helix, so they get their own tight stack instead of inheriting the
          section's gap-4 — that 16px is what Richard flagged as too much. */}
      {(productTitle || selectionSummary) && (
        <div className="flex flex-col gap-1">
          {productTitle && (
            // BL-515: a <p>, not a heading. V2Masthead's h1 already names the
            // product, and a second heading with the same text is flagged by
            // Crownpeak (checkpoint 1.6).
            <p className="font-cormorant text-[32px] font-semibold text-primary">{productTitle}</p>
          )}
          {selectionSummary && <p className="text-sm text-primary/80">{selectionSummary}</p>}
        </div>
      )}

      {/* Price, its struck MSRP, the campaign pill and the Pricing Details /
          Save For Later row are one group on Helix, stacked at gap-2.5. Sizes
          are theirs: 28px bold for the price, 20px light struck for the MSRP. */}
      <div className="flex flex-col gap-2.5" data-block="v2_price_row">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[28px] font-semibold leading-[150%] text-primary">
              {formatPrice(price, currencyCode)}
            </span>
            {hasDiscount && (
              <span className="text-xl font-light leading-[150%] text-primary line-through">
                {formatPrice(fullPrice, currencyCode)}
              </span>
            )}
          </div>

          {/* The pill is a button on Helix: it opens the Zaius campaign modal,
              the same one the utility bar's offer link opens. */}
          {hasDiscount && promotionalCopy && (
            <button
              type="button"
              onClick={openZaiusCampaign}
              aria-haspopup="dialog"
              aria-label={`${promotionalCopy} — see campaign details`}
              // BL-784: comp specs square corners and a #F6F2F0 fill (the
              // `warm-white` token), not sand-light's rounded chip.
              className="cursor-pointer bg-warm-white px-4 py-2 text-primary group"
            >
              <span className="flex items-center justify-center gap-2.5">
                <span className="text-sm leading-[150%] font-normal whitespace-nowrap">{promotionalCopy}</span>
                <InfoIcon className="size-4" />
              </span>
            </button>
          )}
        </div>

        <div className="flex items-center justify-between gap-4">
          {/* Helix renders both of these at text-sm / 400. Ours were 15px/300
              and 16px/400, which read as lopsided across the row. */}
          <PriceDetails />
          {saveForLaterBlock && (
            <SaveForLater block={saveForLaterBlock} align="right" size="sm" />
          )}
        </div>
      </div>

      {show_finance_message && price > 0 && <AffirmLine price={price} />}

      {show_hsa_fsa_message && <HsaMessage align="left" hideOnMobile={false} padded={false} />}

      {showZipCodeChecker && (
        <ZipCodeChecker
          block={zipCodeCheckerBlock}
          initialExpanded={!!show_zip_code_checker_expanded}
          collapsible
          onExpandedChange={setZipExpanded}
        />
      )}

      {showPitstop && (
        <button
          type="button"
          onClick={() => openModal(<V2ExtendedOptionButtonsModal pitstop={pitstop} />, null, 'bg-white')}
          className="flex items-center justify-between gap-4 bg-warm-white p-4 text-left cursor-pointer group"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium leading-5">{pitstop.heading_label}</div>
            {pitstop.description && (
              <div className="pt-1.5 text-sm font-light leading-5 text-primary">{pitstop.description}</div>
            )}
          </div>
          <InfoIcon className="size-4" />
        </button>
      )}

      <AddToCartButton
        block={atcButtonBlock ?? {}}
        className="px-6 py-4 font-sans text-[18px] font-normal"
        radius="rounded-[40px]"
      />

      {/* BL-652 review: at text-sm this wraps to two lines on small phones.
          text-xs keeps it on one, text-sm from md up as before. */}
      {shippingMessage && (
        <p className="flex items-center justify-center gap-2 text-center text-sm text-primary">
          {/* BL-784: comp specs a 16px sun icon here, not the blue status dot. */}
          <SunIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
          {shippingMessage}
        </p>
      )}

      {/* BL-784: the three badge boxes span edge-to-edge (justify-between, no
          row gap); each icon+label sits inset 14.4px inside its own box, so
          the icon itself doesn't touch the row's edge. Icon boxed at 20x20
          with a 4.8px gap to its label. */}
      {usps.length > 0 && (
        <ul role="list" className="flex items-start justify-between pt-2">
          {usps.map((item, index) => (
            <li
              className="flex flex-col items-center gap-[4.8px] px-[14.4px] text-center"
              key={item?._metadata?.uid || index}
            >
              {item?.icon?.url && (
                <Image
                  src={buildContentstackImageUrl(item.icon.url, 48)}
                  // BL-514: decorative next to its label, see V2Usp.
                  alt={item?.usp_text ? '' : (item?.icon?.title || '')}
                  role={item?.usp_text ? 'presentation' : undefined}
                  width={item.icon.dimension?.width || 648}
                  height={item.icon.dimension?.height || 540}
                  sizes="20px"
                  className="size-5 object-contain"
                />
              )}
              {item?.usp_text && (
                <span className="text-xs text-primary">{item.usp_text}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default V2BuyBoxCard;
